import {
  FloatingFocusManager,
  FloatingPortal,
  offset,
  size,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useId,
  useInteractions,
  type MiddlewareState,
} from '@floating-ui/react';
import { styled } from '@porsche-code/kado-styled-system/jsx';
import { token } from '@porsche-code/kado-styled-system/tokens';
import {
  PButtonPure,
  PCheckbox,
  PInputText,
} from '@porsche-design-system/components-react';
import { useVirtualizer } from '@tanstack/react-virtual';
import React, {
  createContext,
  Fragment,
  useContext,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import treeSelectChildrenIconSrc from '../../icons/tree-select-children.svg';
import treeSelectParentIconSrc from '../../icons/tree-select-parent.svg';
import { SpinnerOverlay } from './Spinner';
import { Tooltip } from './Tooltip';

export type MultiOrganizationSelectProps<
  TOrganization extends OrganizationProps,
> = {
  label: string;
  placeholder?: string;
  clearLabel: string;
  language: string;
  isLoading: boolean;
  organizations: TOrganization[];
  organizationIsVisible?: (org: TOrganization) => boolean;
  xMoreLabel: (count: number) => string;
  selectionCountLabel: (count: number) => string;
  value: number[];
  onChange: (value: number[]) => void;
  name?: string;
  inputId?: string;
  required?: boolean;
  errorMessage?: string;
  onBlur?: () => void;
  style?: React.CSSProperties;
  /**
   * @default popover
   */
  variant?: 'popover' | 'flat';
  withoutPortal?: boolean;
};

export const MultiOrganizationSelect = <
  TOrganization extends OrganizationProps,
>({
  label,
  placeholder,
  name,
  required,
  isLoading,
  organizations,
  organizationIsVisible = () => true,
  language,
  xMoreLabel,
  clearLabel,
  selectionCountLabel,
  errorMessage,
  value: selectedIds,
  onChange: setSelectedIds,
  onBlur,
  inputId,
  style,
  variant = 'popover',
  withoutPortal,
}: MultiOrganizationSelectProps<TOrganization>) => {
  const [searchText, setSearchText] = useState('');
  const deferredSearchText = useDeferredValue(searchText);
  const organizationIsVisibleRef = useRef(organizationIsVisible);
  organizationIsVisibleRef.current = organizationIsVisible;

  const data = useMemo(() => {
    const currentOrganizationIsVisible = organizationIsVisibleRef.current;
    const organizationIsHidden = (org: TOrganization) =>
      !currentOrganizationIsVisible(org);

    if (!organizations) return null;

    const organizationsMap = new Map(
      organizations.map((org) => [
        org.id,
        {
          ...org,
          depth: 0,
          children: [] as (typeof org)[],
        },
      ]),
    );

    const organizationsToTop = (
      org?: TOrganization,
    ): (TOrganization & { children: TOrganization[] })[] => {
      const orgFromMap = org && organizationsMap.get(org.id);

      if (
        typeof orgFromMap?.parentId === 'number' &&
        orgFromMap.parentId !== orgFromMap.id
      ) {
        return [
          orgFromMap,
          ...organizationsToTop(organizationsMap.get(orgFromMap.parentId)),
        ];
      }

      if (orgFromMap) {
        return [orgFromMap];
      }

      return [];
    };

    const hasParent = (org: TOrganization) =>
      typeof org.parentId === 'number' && organizationsMap.has(org.parentId);

    const roots = [...organizationsMap.values()]
      .filter(
        (org) =>
          !hasParent(org) ||
          // or is its own parent
          org.id === org.parentId ||
          // or every parent is hidden
          organizationsToTop(org).slice(1).every(organizationIsHidden),
      )
      .filter(currentOrganizationIsVisible)
      .toSorted((a, b) => a.name.localeCompare(b.name));

    // connect children
    organizationsMap.forEach((org) => {
      if (org.parentId && currentOrganizationIsVisible(org)) {
        // find first visible parent
        const parent = organizationsToTop(org)
          .slice(1)
          .find(currentOrganizationIsVisible);

        if (parent && parent !== org) {
          parent.children.push(org);
          parent.children.sort((a, b) => a.name.localeCompare(b.name));
        }
      }
    });

    // calculate depth
    organizationsMap.forEach((org) => {
      let parent = org as typeof org | undefined;
      while (parent?.parentId && parent?.parentId !== parent?.id) {
        parent = organizationsMap.get(parent?.parentId);
        if (!parent || currentOrganizationIsVisible(parent)) {
          org.depth++;
        }
      }
    });

    return { roots, organizationsMap };
  }, [organizations]);

  const searchMatches = useMemo(() => {
    const words = deferredSearchText.toLowerCase().split(/\s+/);
    const matches = new Map<
      number,
      { nameMatches: boolean; subtreeMatches: boolean }
    >();

    if (!data || !deferredSearchText) return matches;

    const findMatches = (org: OrganizationProps): boolean[] => {
      const matchingWords = words.map((word) =>
        org.name.toLowerCase().includes(word),
      );
      const nameMatches = matchingWords.every(Boolean);

      org.children?.forEach((child) => {
        const childMatches = findMatches(child);
        childMatches.forEach((childMatchesWord, index) => {
          matchingWords[index] ||= childMatchesWord;
        });
      });

      matches.set(org.id, {
        nameMatches,
        subtreeMatches: matchingWords.every(Boolean),
      });
      return matchingWords;
    };

    data.roots.forEach(findMatches);
    return matches;
  }, [data, deferredSearchText]);

  const [expandedIds, setExpandedIds] = useState([] as number[]);
  const [isOpen, setIsOpen] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const [inputWidth, setInputWidth] = useState(800);
  const valueText = useMemo(() => {
    if (!data || isLoading) return '';

    const organizationNames = selectedIds
      .map((id) => data.organizationsMap.get(id))
      .filter(<T,>(d: T | undefined): d is T => !!d)
      .toSorted((a, b) => a.depth - b.depth || a.name.localeCompare(b.name))
      .map((org) => org.name);

    const maxWidth = inputWidth / 8; // assume a char is about 8px wide
    const maxElements = Math.max(
      1,
      organizationNames.findIndex(
        (_, idx) =>
          organizationNames.slice(0, idx + 1).join(', ').length > maxWidth,
      ),
    );

    if (organizationNames.length > maxElements + 1) {
      organizationNames.splice(
        maxElements,
        0,
        xMoreLabel(organizationNames.length - maxElements),
      );
    }

    return new Intl.ListFormat(language, { type: 'conjunction' }).format(
      organizationNames.slice(0, maxElements + 1),
    );
  }, [data, isLoading, language, selectedIds, xMoreLabel, inputWidth]);

  useEffect(() => {
    // ideally this should be a dom-resize observer, but seems overkill for now
    setTimeout(() => {
      setInputWidth(inputRef.current?.offsetWidth ?? 800);
    }, 10);
  });

  // This effect checks for mismatch between selectedIds and ids present in organizations
  // if there are ids selected that are not present in the organization array
  // we have to clear them (this is the case e.g. when organizations are hidden), else
  // they will show as selected, but can not be deselected.
  useEffect(() => {
    const existingIds = selectedIds.filter((id) =>
      organizations.some((org) => org.id === id),
    );

    if (
      organizations.length > 0 &&
      !isLoading &&
      existingIds.length !== selectedIds.length
    ) {
      setSelectedIds(existingIds);
    }
  }, [organizations, selectedIds, setSelectedIds, isLoading]);

  const selectedOrganizations = selectedIds
    .map((id) => data?.organizationsMap.get(id))
    .filter(<T,>(d?: T): d is T => !!d);
  const parentRef = useRef<HTMLDivElement>(null);

  // oxlint-disable-next-line react/incompatible-library (Note: will be supported in TanStack virtual v9)
  const rowVirtualizer = useVirtualizer({
    count: selectedOrganizations.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 40,
  });

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: (open, event, reason) => {
      if (
        reason === 'focus' &&
        event &&
        'relatedTarget' in event &&
        event.relatedTarget &&
        refs.floating.current?.parentNode?.contains(event.relatedTarget as Node)
      ) {
        // prevent closing if focus shifts to nested floating element
        return;
      }

      setIsOpen(open);

      if (open) {
        setSearchText('');
        if (inputRef.current) {
          inputRef.current.value = '';
        }
      } else {
        inputRef.current?.blur();
      }
    },
    strategy: withoutPortal ? 'absolute' : undefined,
    placement: 'bottom-start',
    middleware: [
      offset(8),
      size({
        apply(state: MiddlewareState) {
          state.elements.floating.style.width = `${state.rects.reference.width}px`;
        },
      }),
    ],
  });

  const dismiss = useDismiss(context);
  const click = useClick(context, { keyboardHandlers: false });
  const focus = useFocus(context);
  const { getFloatingProps, getReferenceProps } = useInteractions([
    click,
    focus,
    dismiss,
  ]);

  const headingId = useId();
  const multiOrganizationContextValue = useMemo(
    () => ({
      expandedIds,
      setExpandedIds: (ids: number[]) => setExpandedIds([...new Set(ids)]),
      selectedIds,
      setSelectedIds: (ids: number[]) => {
        setSelectedIds([...new Set(ids)]);
        if (deferredSearchText) {
          setExpandedIds([]);
        }
      },
      searchText: deferredSearchText,
      searchMatches,
    }),
    [
      deferredSearchText,
      expandedIds,
      searchMatches,
      selectedIds,
      setSelectedIds,
    ],
  );

  const content = (
    <Content flat={variant === 'flat'}>
      {isLoading && <SpinnerOverlay size="lg" />}

      <OrganizationTree>
        {data?.roots.map((org) => (
          <CheckboxTreeItem
            key={org.id}
            id={org.id}
            level={0}
            label={org.name}
            // oxlint-disable-next-line react/no-children-prop
            children={org.children}
            suffix={org.partnerNumber}
          />
        ))}
      </OrganizationTree>

      <Divider />

      <SelectionTree ref={parentRef}>
        <SelectionHeading>
          {selectionCountLabel(selectedIds.length)}
          <PButtonPure
            type="button"
            icon="none"
            onClick={() => setSelectedIds([])}
          >
            {clearLabel}
          </PButtonPure>
        </SelectionHeading>

        <div
          style={{
            height: rowVirtualizer.getTotalSize(),
            position: 'relative',
            width: '100%',
          }}
        >
          {rowVirtualizer
            .getVirtualItems()
            .map(({ index, size: height, start, key }) => {
              const organization = selectedOrganizations[index];
              return (
                <SelectionItem
                  data-index={index}
                  ref={rowVirtualizer.measureElement}
                  key={key}
                  virtualized
                  style={{
                    height: `${height}px`,
                    transform: `translateY(${start}px)`,
                  }}
                >
                  {organization.name}
                  <PButtonPure
                    type="button"
                    icon="delete"
                    onClick={() =>
                      setSelectedIds(
                        selectedIds.filter((i) => i !== organization.id),
                      )
                    }
                    hideLabel
                  />
                </SelectionItem>
              );
            })}
        </div>
      </SelectionTree>
    </Content>
  );

  const FloatingContainer = withoutPortal ? Fragment : FloatingPortal;

  return (
    <Container data-testid={inputId && `select-${inputId}`}>
      <PInputText
        ref={(ref) => {
          refs.setReference(ref);
          inputRef.current = ref as HTMLInputElement;
        }}
        {...getReferenceProps()}
        loading={isLoading}
        label={label}
        aria-label={label}
        // Explicit opt out of form association while open to prevent PDS submitting
        form={isOpen ? '' : undefined}
        state={errorMessage ? 'error' : 'none'}
        message={errorMessage}
        style={style}
        name={name ?? `select-${inputId}`}
        required={required}
        autoComplete="off"
        // Keep typing independent from the deferred organization-tree render.
        value={isOpen ? undefined : valueText}
        placeholder={(isOpen ? valueText : undefined) || placeholder}
        onInput={(evt) => setSearchText((evt.target as HTMLInputElement).value)}
        onKeyDown={(evt) => {
          if (evt.key === 'Enter') {
            const [match, ...others] = [
              ...(data?.organizationsMap.values() ?? []),
            ].filter((org) =>
              searchText
                .split(/\s+/)
                .every((word) => nameMatches(org.name, word)),
            );

            if (others.length === 0 && match) {
              setSelectedIds([
                ...new Set([
                  ...selectedIds,
                  match.id,
                  ...match.children.flatMap(flattenChildren).map((o) => o.id),
                ]),
              ]);

              setExpandedIds([]);
              setSearchText('');
              const inputEl = inputRef.current;
              if (inputEl) inputEl.value = '';
            }
          }
        }}
        onBlur={() => onBlur?.()}
      />
      <MultiOrganizationContext.Provider value={multiOrganizationContextValue}>
        {variant === 'flat'
          ? content
          : isOpen && (
              <FloatingContainer>
                <FloatingFocusManager
                  context={context}
                  modal={false}
                  initialFocus={inputRef}
                  returnFocus={false}
                >
                  <FloatingContent
                    aria-labelledby={headingId}
                    ref={refs.setFloating}
                    style={floatingStyles}
                    {...getFloatingProps()}
                  >
                    {content}
                  </FloatingContent>
                </FloatingFocusManager>
              </FloatingContainer>
            )}
      </MultiOrganizationContext.Provider>
    </Container>
  );
};

const MultiOrganizationContext = createContext<{
  expandedIds: number[];
  setExpandedIds: (ids: number[]) => void;
  selectedIds: number[];
  setSelectedIds: (ids: number[]) => void;
  searchText: string;
  searchMatches: ReadonlyMap<
    number,
    { nameMatches: boolean; subtreeMatches: boolean }
  >;
}>({
  expandedIds: [],
  setExpandedIds: () => void 0,
  selectedIds: [],
  setSelectedIds: () => void 0,
  searchText: '',
  searchMatches: new Map(),
});

type OrganizationProps = {
  id: number;
  name: string;
  parentId?: number | null | undefined;
  children?: OrganizationProps[];
  partnerNumber?: string | null;
};

const flattenChildren = (orga: OrganizationProps): OrganizationProps[] => [
  orga,
  ...(orga.children?.flatMap(flattenChildren) ?? []),
];

const nameMatches = (name: string, needle: string): boolean =>
  name.toLowerCase().includes(needle.toLowerCase());

const CheckboxTreeItem = React.memo(
  ({
    level,
    id,
    label,
    suffix,
    children,
  }: {
    level: number;
    id: number;
    label: string;
    suffix?: string | null;
    children?: OrganizationProps[];
  }) => {
    const {
      expandedIds,
      setExpandedIds,
      selectedIds,
      setSelectedIds,
      searchText,
      searchMatches,
    } = useContext(MultiOrganizationContext);

    const descendants = useMemo(
      () => children?.flatMap(flattenChildren) ?? [],
      [children],
    );

    const isSearching = !!searchText;
    const organizationMatches = searchMatches.get(id);
    const nameMatchesSearch = organizationMatches?.nameMatches ?? false;
    const nameOrChildrenMatchSearch =
      organizationMatches?.subtreeMatches ?? false;

    const collapsed =
      !expandedIds.includes(id) &&
      // if we are in search-mode, collapse this item only if no match is found
      (isSearching ? !nameOrChildrenMatchSearch : true);

    if (isSearching && !nameOrChildrenMatchSearch) {
      // hide element if in search-mode and no match found
      return null;
    }

    const showThisItem = !isSearching || nameMatchesSearch;

    const showCollapseButton = !isSearching && (children?.length ?? 0) > 0;

    return (
      <>
        {showThisItem && (
          <TreeItem style={{ paddingLeft: isSearching ? 0 : level * 24 }}>
            {showCollapseButton ? (
              <ArrowButton
                type="button"
                icon={collapsed ? 'arrow-head-right' : 'arrow-head-down'}
                onClick={() =>
                  setExpandedIds(
                    collapsed
                      ? [...expandedIds, id]
                      : expandedIds.filter((i) => i !== id),
                  )
                }
                hideLabel
              >
                {label}
              </ArrowButton>
            ) : (
              <div />
            )}

            {descendants.length > 0 ? (
              <Tooltip
                content={
                  <ButtonGroup>
                    <PButtonPure
                      type="button"
                      iconSource={treeSelectChildrenIconSrc}
                      hideLabel
                      onClick={() => {
                        const hasDescendants = descendants.every((descendant) =>
                          selectedIds.some(
                            (selectedId) => descendant.id === selectedId,
                          ),
                        );

                        setSelectedIds(
                          hasDescendants
                            ? selectedIds.filter((selectedId) =>
                                descendants.every(
                                  (descendant) => descendant.id !== selectedId,
                                ),
                              )
                            : [
                                ...new Set([
                                  ...selectedIds,
                                  ...descendants.map(
                                    (descendant) => descendant.id,
                                  ),
                                ]),
                              ],
                        );
                      }}
                    />
                    <PButtonPure
                      type="button"
                      iconSource={treeSelectParentIconSrc}
                      hideLabel
                      onClick={() => {
                        setSelectedIds(
                          selectedIds.includes(id)
                            ? selectedIds.filter(
                                (selectedId) => selectedId !== id,
                              )
                            : [...selectedIds, id],
                        );
                      }}
                    />
                  </ButtonGroup>
                }
                placement="top-start"
                safePolygonClose
              >
                <PCheckbox
                  label={label}
                  aria-label={label}
                  checked={selectedIds.includes(id)}
                  onChange={(event) => {
                    const { checked } = event.target as HTMLInputElement;
                    setSelectedIds(
                      checked
                        ? [
                            ...selectedIds,
                            id,
                            ...descendants.map((org) => org.id),
                          ]
                        : selectedIds.filter(
                            (i) =>
                              i !== id &&
                              !descendants.some((org) => org.id === i),
                          ),
                    );
                  }}
                />
              </Tooltip>
            ) : (
              <PCheckbox
                label={label}
                aria-label={label}
                checked={selectedIds.includes(id)}
                onChange={(event) => {
                  const { checked } = event.target as HTMLInputElement;
                  setSelectedIds(
                    checked
                      ? [
                          ...selectedIds,
                          id,
                          ...descendants.map((org) => org.id),
                        ]
                      : selectedIds.filter(
                          (i) =>
                            i !== id &&
                            !descendants.some((org) => org.id === i),
                        ),
                  );
                }}
              />
            )}
            {suffix ? <Suffix>{suffix}</Suffix> : <div />}
          </TreeItem>
        )}
        {!collapsed &&
          children?.map((org) => (
            <CheckboxTreeItem
              key={org.id}
              id={org.id}
              label={org.name}
              level={level + 1}
              // oxlint-disable-next-line react/no-children-prop
              children={org.children}
              suffix={org.partnerNumber}
            />
          ))}
      </>
    );
  },
);

const Container = styled('div', {
  base: {
    position: 'relative',
  },
});

const FloatingContent = styled('div', {
  base: {
    minWidth: 500,
    minHeight: 350,
    maxHeight: 350,
    backgroundColor: 'background.canvas',
    display: 'grid',
    borderRadius: 'xl',
    zIndex: 'popover',
    shadow: 'md',
    overflow: 'hidden',
  },
});

const ButtonGroup = styled('div', {
  base: {
    display: 'flex',
    gap: 'sm',
  },
});

const Content = styled('div', {
  base: {
    display: 'grid',
    gridTemplateColumns: '1fr 1px 1fr',
    minHeight: 350,
    maxHeight: 350,
    overflow: 'hidden',
  },

  variants: {
    flat: {
      true: { height: 350, paddingTop: 'sm' },
    },
  },
});

const Divider = styled('div', {
  base: {
    backgroundColor: 'background.surface',
  },
});

const OrganizationTree = styled('div', {
  base: {
    px: 'md',
    overflowY: 'auto',
    height: '100%',
  },
});

const SelectionTree = styled('div', {
  base: {
    px: 'md',
    overflowY: 'auto',
    height: '100%',
  },
});

const SelectionHeading = styled('strong', {
  base: {
    position: 'sticky',
    top: 0,
    backgroundColor: 'background.canvas',
    mx: `calc(${token('spacing.md')} * -1)`,
    padding: 'md',
    paddingBottom: 'sm',
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    zIndex: 'default',
  },
});

const TreeItem = styled('div', {
  base: {
    display: 'grid',
    gridTemplateColumns: '24px 1fr auto',
    py: 'sm',
    gap: 'xs',
    '&:first-child': {
      marginTop: `calc(${token('spacing.md')} - ${token('spacing.sm')})`,
    },
  },
});

const SelectionItem = styled('div', {
  base: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    py: 'sm',
    gap: 'xs',
  },

  variants: {
    virtualized: {
      true: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        boxSizing: 'border-box',
      },
    },
  },
});

const Suffix = styled('div', {
  base: {
    color: 'text.contrastMedium',
    '&:before': { content: '"("' },
    '&:after': { content: '")"' },
  },
});

const ArrowButton = styled(PButtonPure, {
  base: {
    justifySelf: 'start',
    alignSelf: 'center',
  },
});
